/**
 * ورود مخاطبین از فایل اکسل — the congress spreadsheet import.
 *
 * TWO STEPS, AND THE FIRST ONE WRITES NOTHING. Choosing a file previews it;
 * only «تأیید و ثبت» commits. The preview is not a summary computed here — it
 * is the server's own plan, row by row, and the commit applies that same plan.
 * A client that computed its own preview would eventually disagree with the
 * server, and the client's is the one the user would have believed (the
 * `previewSend` rule, CLAUDE.md §12).
 *
 * The per-row table is the point of the screen. «۷۶ مخاطب اضافه شد» is not
 * enough to approve an irreversible-ish bulk write: the operator needs to see
 * WHICH row was refused and why, and which contact a duplicate attached to.
 */
import { Button, TBody, TD, TH, THead, TR, Table } from '@meshhub/design-tokens/ui';
import { AlertTriangle, Check, FileUp, Info } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { SettingsPage } from '../../components/settings/SettingsKit.web.jsx';
import { formatCount, isoToJalaliDate, toPersianDigits } from '../../lib/format.js';
import { importContacts } from '../../lib/imports.js';
import { useSession } from '../../lib/session.jsx';
import { S } from '../../lib/strings.js';

const OUTCOME_STYLE = {
  CREATED: 'text-success',
  MATCHED: 'text-text',
  SKIPPED: 'text-text-muted',
  INVALID: 'text-danger',
};

/**
 * Only a row that would WRITE something can be selected.
 *
 * INVALID has nothing to import and SKIPPED is already on file, so a checkbox
 * beside either would be a control that cannot change the outcome — the same
 * reason the send dialog hides the force toggle beside an OPT_OUT
 * (CLAUDE.md §12).
 */
function isSelectable(row) {
  return row.outcome === 'CREATED' || row.outcome === 'MATCHED';
}

function Stat({ label, value, tone }) {
  return (
    <div className="flex flex-col gap-1 rounded border border-border bg-surface px-4 py-3">
      <span className="text-xs text-text-muted">{label}</span>
      <span className={`text-lg font-bold ${tone ?? ''}`}>{formatCount(value)}</span>
    </div>
  );
}


/**
 * Each of these shows the CELL as written, and underneath it what the import
 * read it as — but only when the two differ. «رجبی» under «آقای دکتر رجبی» is
 * worth a line; «تهران» under «تهران» is noise.
 */
function NameCell({ row }) {
  const written = row.source?.name ?? '';
  const read = row.parsed?.lastName ?? '';
  const gender = row.parsed?.gender ? S.imports.genders[row.parsed.gender] : null;
  const same = written === read && !gender;
  return (
    <div className="flex flex-col">
      <span>{written}</span>
      {!same && (
        <span className="text-xs text-text-muted">
          {S.imports.readAs}: {read || '—'}
          {gender ? ` · ${gender}` : ''}
        </span>
      )}
    </div>
  );
}

function DateCell({ row }) {
  const written = row.source?.date ?? '';
  const iso = row.parsed?.occurredAt;
  return (
    <div className="flex flex-col">
      <span>{written}</span>
      {iso ? (
        // Converted back to Jalali rather than shown as ISO: it proves the
        // round trip AND is the calendar the operator reads.
        <span className="text-xs text-text-muted">
          {S.imports.readAs}: {isoToJalaliDate(iso)}
        </span>
      ) : (
        written && <span className="text-xs text-danger">{S.imports.notRead}</span>
      )}
    </div>
  );
}

function PhoneCell({ row }) {
  const written = row.source?.phone ?? '';
  const read = row.parsed?.phone ?? '';
  return (
    <div className="flex flex-col">
      <span>{toPersianDigits(written)}</span>
      {read && read !== written && (
        <span className="text-xs text-text-muted">
          {S.imports.readAs}: {read}
        </span>
      )}
    </div>
  );
}

export function ImportScreen() {
  const { isAdmin } = useSession();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [committed, setCommitted] = useState(false);
  const [busy, setBusy] = useState(null); // 'preview' | 'commit'
  const [error, setError] = useState(null);
  //: Row numbers the operator wants imported. Seeded from the preview with
  //: everything importable already ticked — the common case is "all of it",
  //: and making somebody tick 84 boxes to get there would be hostile.
  const [selected, setSelected] = useState(() => new Set());

  if (!isAdmin) {
    return (
      <SettingsPage title={S.imports.title}>
        <p className="text-sm text-text-muted">{S.imports.onlyAdmin}</p>
      </SettingsPage>
    );
  }

  async function run(mode, chosen = file) {
    if (!chosen) return;
    setBusy(mode);
    setError(null);
    try {
      const result = await importContacts(chosen, {
        mode,
        rows: mode === 'commit' ? [...selected] : undefined,
      });
      setPlan(result);
      setCommitted(mode === 'commit');
      if (mode === 'preview') {
        setSelected(new Set(result.rows.filter(isSelectable).map((r) => r.rowNumber)));
      }
    } catch (err) {
      setError(err.message);
      if (mode === 'preview') setPlan(null);
    } finally {
      setBusy(null);
    }
  }

  function choose(event) {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    setFile(chosen);
    setPlan(null);
    setCommitted(false);
    setSelected(new Set());
    run('preview', chosen);
  }

  function reset() {
    setFile(null);
    setPlan(null);
    setCommitted(false);
    setError(null);
    setSelected(new Set());
    if (inputRef.current) inputRef.current.value = '';
  }

  const selectableRows = (plan?.rows ?? []).filter(isSelectable);

  function toggleRow(rowNumber) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(rowNumber)) next.delete(rowNumber);
      else next.add(rowNumber);
      return next;
    });
  }

  return (
    <SettingsPage
      title={S.imports.title}
      toolbar={
        plan && !committed ? (
          <>
            <Button variant="ghost" onClick={reset} disabled={busy !== null}>
              {S.imports.again}
            </Button>
            <Button
              onClick={() => run('commit')}
              disabled={busy !== null || selected.size === 0}
            >
              {busy === 'commit' ? S.imports.committing : S.imports.commit}
            </Button>
          </>
        ) : plan && committed ? (
          <Button variant="ghost" onClick={reset}>
            {S.imports.again}
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-6">
        {!plan && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text-muted">{S.imports.intro}</p>
            <p className="text-xs text-text-muted">{S.imports.columns}</p>
            <div>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={choose}
                className="hidden"
                id="import-file"
              />
              <Button onClick={() => inputRef.current?.click()} disabled={busy !== null}>
                <FileUp className="size-4" />
                {busy === 'preview' ? S.imports.previewing : S.imports.choose}
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded border border-danger bg-surface px-4 py-3 text-sm text-danger">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {plan && (
          <>
            <div
              className={`flex items-start gap-2 rounded border px-4 py-3 text-sm ${
                committed
                  ? 'border-success bg-surface text-success'
                  : 'border-border bg-surface text-text-muted'
              }`}
            >
              {committed ? (
                <Check className="mt-0.5 size-4 shrink-0" />
              ) : (
                <Info className="mt-0.5 size-4 shrink-0" />
              )}
              <span>{committed ? S.imports.done : S.imports.previewOnly}</span>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="text-text-muted">
                {S.imports.file}: <b className="text-text">{file?.name}</b>
              </span>
              <span className="text-text-muted">
                {S.imports.sheet}: <b className="text-text">{plan.sheet}</b>
              </span>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <Stat label={S.imports.created} value={plan.created} tone="text-success" />
              <Stat label={S.imports.matched} value={plan.matched} />
              <Stat label={S.imports.skipped} value={plan.skipped} tone="text-text-muted" />
              <Stat
                label={S.imports.invalid}
                value={plan.invalid}
                tone={plan.invalid > 0 ? 'text-danger' : undefined}
              />
            </div>

            {!committed && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className={selected.size === 0 ? 'text-danger' : 'text-text'}>
                  {selected.size === 0
                    ? S.imports.nothingSelected
                    : S.imports.selectedOf
                        .replace('{n}', formatCount(selected.size))
                        .replace('{total}', formatCount(selectableRows.length))}
                </span>
                <span className="text-xs text-text-muted">{S.imports.selectionNote}</span>
              </div>
            )}

            {plan.newSources?.length > 0 && (
              <p className="text-sm text-text-muted">
                {S.imports.newSources}: <b className="text-text">{plan.newSources.join('، ')}</b>
              </p>
            )}

            {plan.newInteractionType && (
              <p className="text-sm text-text-muted">
                {S.imports.newInteractionType}:{' '}
                <b className="text-text">{plan.newInteractionType}</b>
              </p>
            )}

            <div>
              <h2 className="mb-2 text-sm font-bold">{S.imports.rowsTitle}</h2>
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH className="w-thumb">
                        <input
                          type="checkbox"
                          className="accent-primary"
                          aria-label={S.imports.selectAll}
                          disabled={committed || selectableRows.length === 0}
                          checked={
                            selectableRows.length > 0 && selected.size === selectableRows.length
                          }
                          ref={(el) => {
                            // Indeterminate is a PROPERTY, not an attribute:
                            // React cannot set it from JSX.
                            if (el) {
                              el.indeterminate =
                                selected.size > 0 && selected.size < selectableRows.length;
                            }
                          }}
                          onChange={(event) =>
                            setSelected(
                              event.target.checked
                                ? new Set(selectableRows.map((r) => r.rowNumber))
                                : new Set(),
                            )
                          }
                        />
                      </TH>
                      <TH className="w-14">{S.imports.row}</TH>
                      <TH>{S.imports.cols.name}</TH>
                      <TH>{S.imports.cols.congress}</TH>
                      <TH>{S.imports.cols.date}</TH>
                      <TH>{S.imports.cols.description}</TH>
                      <TH>{S.imports.cols.phone}</TH>
                      <TH className="w-52">{S.imports.outcome}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {plan.rows.map((row) => (
                      <TR key={row.rowNumber}>
                        <TD>
                          <input
                            type="checkbox"
                            className="accent-primary"
                            aria-label={`${S.imports.selectRow} ${row.rowNumber}`}
                            disabled={committed || !isSelectable(row)}
                            title={isSelectable(row) ? undefined : S.imports.notSelectable}
                            checked={selected.has(row.rowNumber)}
                            onChange={() => toggleRow(row.rowNumber)}
                          />
                        </TD>
                        <TD className="text-text-muted">{formatCount(row.rowNumber)}</TD>
                        <TD>
                          <NameCell row={row} />
                        </TD>
                        <TD>{row.source?.congress}</TD>
                        <TD>
                          <DateCell row={row} />
                        </TD>
                        <TD className="max-w-md truncate" title={row.source?.description}>
                          {row.source?.description}
                        </TD>
                        <TD className="text-text-muted">
                          <PhoneCell row={row} />
                        </TD>
                        <TD className={OUTCOME_STYLE[row.outcome]}>
                          <div className="flex flex-col gap-1">
                            <span>{S.imports.outcomes[row.outcome] ?? row.outcome}</span>
                            {row.detail && (
                              <span className="text-xs text-text-muted">{row.detail}</span>
                            )}
                            {row.warnings?.map((w) => (
                              <span key={w} className="flex items-center gap-1 text-xs text-warning">
                                <AlertTriangle className="size-3 shrink-0" />
                                {w}
                              </span>
                            ))}
                          </div>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </div>
    </SettingsPage>
  );
}
